// PostDraftItem.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import UploadModalMediaThumbnail from '../container/UploadModalMediaThumbnail.js';
import ProfileCardItem from '../container/ProfileCardItem.js';
import MessageProgressBar from '../component/MessageProgressBar.jsx';
import {
  Status as UploadStatus,
  Entry as UploadJobType,
} from '../resource/uploadConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { color, textColor } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import MoreIconSource from '../../img/ic_topbar_more_grey.svg';

export const PostDraftItem = ({
  messageId,
  assetId,
  uploadJobId,
  uploadJobStatus,
  uploadJobType,
  statusI18nKey,
  averageProgress,
  t,
  toggleUploadJobMoreModal,
}) => {
  const isFailed = [
    UploadStatus.UPLOAD_FAILED,
    UploadStatus.PROCESSING_FAILED,
    UploadStatus.REVIEW_FAILED,
    UploadStatus.DELIVERY_FAILED,
    UploadStatus.FAILED,
  ].includes(uploadJobStatus);
  const isReviewing = [
    UploadStatus.REVIEW_STARTED,
    UploadStatus.REVIEW_COMPLETED,
    UploadStatus.DELIVERY_STARTED,
  ].includes(uploadJobStatus);
  if (UploadJobType.CLIP === uploadJobType) {
    return (
      <ProfileCardItem messageId={messageId} listType={ProfileListType.SHORT} />
    );
  }
  return (
    <StyledPostUploadItem
      isDeleting={UploadStatus.DELETING === uploadJobStatus}
      onClick={() => {
        toggleUploadJobMoreModal({ uploadJobId, isOpen: true });
        trackMixpanel({
          type: EventTypes.USER_CLICKED,
          payload: {
            viewId: getViewId({
              pathname: window.location.pathname,
            }),
            buttonId: ButtonId.All.ButtonMore,
          },
        });
      }}
    >
      <ItemWrapper>
        <PreviewWrapper>
          <UploadModalMediaThumbnail assetId={assetId} />
        </PreviewWrapper>
        <Status
          color={
            isFailed
              ? textColor.danger
              : isReviewing
              ? textColor.secondary
              : textColor.black
          }
        >
          {t(statusI18nKey)}
        </Status>
        <MoreButton>
          <img src={MoreIconSource} />
        </MoreButton>
      </ItemWrapper>
      <MessageProgressBar
        max={100}
        value={averageProgress}
        color={isFailed ? color.red : isReviewing ? color.sky : color.tealBlue}
      />
    </StyledPostUploadItem>
  );
};

PostDraftItem.propTypes = {
  messageId: PropTypes.string,
  assetId: PropTypes.string,
  uploadJobId: PropTypes.string,
  uploadJobStatus: PropTypes.string,
  uploadJobType: PropTypes.string,
  statusI18nKey: PropTypes.string,
  averageProgress: PropTypes.number,
  t: PropTypes.func.isRequired,
  toggleUploadJobMoreModal: PropTypes.func,
};

PostDraftItem.defaultProps = {
  messageId: null,
  assetId: null,
  uploadJobId: null,
  uploadJobStatus: UploadStatus.PENDING,
  uploadJobType: null,
  statusI18nKey: '',
  averageProgress: 0,
  toggleUploadJobMoreModal: () => null,
};

const StyledPostUploadItem = styled.button.attrs(({ isDeleting }) => ({
  style: {
    height: `${isDeleting ? 0 : 58}px`,
  },
}))`
  ${ResetButtonStyle};
  width: 100%;
  transition: height 0.6s;
  overflow: hidden;
`;

const ItemWrapper = styled.div`
  padding: 0px 16px;
  height: 56px;
  display: flex;
  align-items: center;
`;

const PreviewWrapper = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${color.grey['700']};
`;

const Status = styled.div.attrs(({ color }) => ({
  style: {
    color,
  },
}))`
  ${TextEllipsis};
  padding: 0px 8px;
  flex: auto;
  font-size: 14px;
  text-align: left;
`;

const MoreButton = styled.div`
  width: 24px;
  height: 24px;
  flex: none;
  > img {
    width: 100%;
    height: 100%;
  }
`;

export default withTranslation()(PostDraftItem);
